// ============================================================
// FICHIER : MobilityMarketUS.tsx
// Port fidèle du moteur 3D de MobilityMarket.tsx (India) : caméra
// orbitale Three.js, tuiles CARTO dark_all au sol, hexagones H3
// extrudés (hauteur + couleur ~ volume de trips), hover raycasting,
// légende par quantiles, contrôles de zoom, marqueur "fly-to" pour la
// recherche de ville.
//
// Différence volontaire avec India : ce composant est conçu uniquement
// pour l'usage plein-écran intégré à StoryUS (pas de double-usage
// "page dédiée" comme MobilityMarket.tsx en Inde) — donc pas de panneau
// de titre/stat interne à masquer en CSS ; seuls canvas + légende +
// zoom sont rendus, le texte narratif vient de StoryUS lui-même.
//
// Palette : "blue shades" (au lieu du teal India) pour différencier
// visuellement l'identité US tout en restant dans la charte ATR.
// ============================================================

import { useEffect, useRef, useState } from "react";
// @ts-ignore
import * as THREE from "three";
import { cellToLatLng } from "h3-js";
import { US_MAP_CENTER_LAT, US_MAP_CENTER_LNG, FocusCity } from "../config/carto.us";
import { fetchUSMobilityHex, USHexFilters } from "../layers/fetchLayers.us";
import MapLoadingOverlay from "./MapLoadingOverlay";
import "./MobilityMarketUS.css";
import { cartoBasemapTileUrl } from "../config/carto";

const CENTER_LAT = US_MAP_CENTER_LAT;
const CENTER_LNG = US_MAP_CENTER_LNG;
const WORLD_TILE = 380;
const ZOOM = 6;

// ── Cadrage par défaut de la caméra ──
// Le monde 3D est construit avec pour origine (x=0) la longitude
// US_MAP_CENTER_LNG (-87.5°, région Ohio). Pour que le CONUS entier tienne
// dans le cadre ET soit décalé vers la droite (pour laisser la place au
// panneau de texte à gauche), la caméra doit :
//   1. Regarder un point plus à l'OUEST que le centre géographique réel du
//      pays (~-98.5°) — ~20° plus à l'ouest de la référence, soit environ
//      -676 unités monde (33.8 unités/degré à ce niveau de zoom) — ce qui
//      pousse visuellement les États-Unis vers la droite de l'écran.
//   2. Être suffisamment reculée (distance) pour englober toute la largeur
//      du pays (côte Pacifique à côte Atlantique, ~58° soit ~1950 unités).
const DEFAULT_DIST = 3200;
const DEFAULT_THETA = 0;
const DEFAULT_PHI = 0.2;
const DEFAULT_TARGET = { x: -650, z: 40 };

function project(lat: number, lng: number): [number, number] {
  const n = Math.pow(2, ZOOM);
  const tileX = ((lng + 180) / 360) * n;
  const tileY =
    ((1 -
      Math.log(Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)) /
        Math.PI) /
      2) *
    n;
  const centerTileX = ((CENTER_LNG + 180) / 360) * n;
  const centerTileY =
    ((1 -
      Math.log(
        Math.tan((CENTER_LAT * Math.PI) / 180) + 1 / Math.cos((CENTER_LAT * Math.PI) / 180)
      ) /
        Math.PI) /
      2) *
    n;
  return [(tileX - centerTileX) * WORLD_TILE, (tileY - centerTileY) * WORLD_TILE];
}

function latLngToTile(lat: number, lng: number, zoom: number) {
  const n = Math.pow(2, zoom);
  const x = Math.floor(((lng + 180) / 360) * n);
  const y = Math.floor(
    ((1 -
      Math.log(Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)) /
        Math.PI) /
      2) *
      n
  );
  return { x, y };
}

// ─── Palette "blue shades" — 7 paliers, du plus clair au plus foncé ───
const DEFAULT_THRESHOLDS = [
  { min: 0, max: 2000, label: "0 – 2k" },
  { min: 2000, max: 4000, label: "2 – 4k" },
  { min: 4000, max: 8000, label: "4 – 8k" },
  { min: 8000, max: 16000, label: "8 – 16k" },
  { min: 16000, max: 32000, label: "16 – 32k" },
  { min: 32000, max: 70000, label: "32 – 70k" },
  { min: 70000, max: Infinity, label: "Above 70k" },
];

// ─── Palette "blue shades" — dégradé lisse foncé -> clair, même structure
// que le tableau teal d'India (interpolation continue dans une seule
// teinte), ancré sur 3 tokens ATR officiels : newMoon (le plus foncé,
// petites valeurs) -> blue -> lightBlue (le plus clair, grandes valeurs).
const DEFAULT_COLORS = [
  "#003d33",
  "#005c4d",
  "#007a66",
  "#009983",
  "#33b39e",
  "#66ccb5",
  "#99e6d5",
];

export interface MobilityMarketUSHandle {
  zoomToCity: (city: FocusCity) => void;
  resetView: () => void;
}

interface Props {
  filters: USHexFilters;
  onReady?: (handle: MobilityMarketUSHandle) => void;
  onStats?: (stats: { hexCount: number; totalWeekly: number; loading: boolean }) => void;
  /**
   * When false (the default embedded-in-story use case), the canvas does
   * NOT capture the mouse wheel for camera zoom — wheel events pass through
   * untouched to the page, so scrolling the story while the cursor is over
   * the 3D map just scrolls the page instead of fighting it. Set true for
   * a standalone full-window usage where wheel-to-zoom is expected.
   */
  allowWheelZoom?: boolean;
}

export default function MobilityMarketUS({ filters, onReady, onStats, allowWheelZoom = true }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const distRef = useRef(DEFAULT_DIST);
  const thetaRef = useRef(DEFAULT_THETA);
  const phiRef = useRef(DEFAULT_PHI);
  const targetRef = useRef({ ...DEFAULT_TARGET });
  const updateCameraRef = useRef<() => void>(() => {});
  const hoveredMeshRef = useRef<any>(null);
  const hoveredOrigColorRef = useRef<any>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const markerRef = useRef<any>(null);
  const barsRef = useRef<any[]>([]);

  const [loading, setLoading] = useState(true);
  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    weekly: number;
    city?: string;
    state?: string;
  } | null>(null);

  const [legendData, setLegendData] = useState<
    { label: string; color: string; min: number; max: number }[]
  >(
    DEFAULT_THRESHOLDS.map((t, i) => ({
      label: t.label,
      color: DEFAULT_COLORS[i],
      min: t.min,
      max: t.max,
    }))
  );

  const interpolateColor = (color1: string, color2: string, t: number) => {
    const c1 = new THREE.Color(color1);
    const c2 = new THREE.Color(color2);
    return c1.clone().lerp(c2, t).getStyle();
  };

  // ─── Marqueur "fly-to" pour la recherche de ville ───
  const addCityMarker = (lat: number, lng: number) => {
    removeMarker();
    const scene = sceneRef.current;
    if (!scene) return;
    const [x, z] = project(lat, lng);

    const outerRingGeo = new THREE.RingGeometry(10, 20, 48);
    const outerRingMat = new THREE.MeshBasicMaterial({
      color: 0x3d97cc,
      transparent: true,
      opacity: 0.8,
      side: THREE.DoubleSide,
    });
    const outerRing = new THREE.Mesh(outerRingGeo, outerRingMat);
    outerRing.position.set(x, 2, z);
    outerRing.rotation.x = -Math.PI / 2;
    scene.add(outerRing);

    const innerRingGeo = new THREE.RingGeometry(4, 10, 32);
    const innerRingMat = new THREE.MeshBasicMaterial({
      color: 0x3d97cc,
      transparent: true,
      opacity: 0.9,
      side: THREE.DoubleSide,
    });
    const innerRing = new THREE.Mesh(innerRingGeo, innerRingMat);
    innerRing.position.set(x, 2, z);
    innerRing.rotation.x = -Math.PI / 2;
    scene.add(innerRing);

    const dotGeo = new THREE.CircleGeometry(3, 16);
    const dotMat = new THREE.MeshBasicMaterial({
      color: 0x3d97cc,
      transparent: true,
      opacity: 1,
      side: THREE.DoubleSide,
    });
    const dot = new THREE.Mesh(dotGeo, dotMat);
    dot.position.set(x, 2.1, z);
    dot.rotation.x = -Math.PI / 2;
    scene.add(dot);

    const lineGeo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(x, 0, z),
      new THREE.Vector3(x, 8, z),
    ]);
    const lineMat = new THREE.LineBasicMaterial({ color: 0x3d97cc, transparent: true, opacity: 0.4 });
    const line = new THREE.Line(lineGeo, lineMat);
    scene.add(line);

    const startTime = Date.now();
    const animateMarker = () => {
      if (!sceneRef.current) return;
      const elapsed = (Date.now() - startTime) / 1000;
      const pulse = 0.5 + 0.5 * Math.sin(elapsed * 2.5);
      outerRing.material.opacity = 0.3 + 0.5 * pulse;
      const scaleRing = 1 + 0.3 * Math.sin(elapsed * 2.5);
      outerRing.scale.set(scaleRing, scaleRing, scaleRing);
      dot.material.opacity = 0.6 + 0.4 * Math.sin(elapsed * 3);
      requestAnimationFrame(animateMarker);
    };
    animateMarker();

    markerRef.current = { outerRing, innerRing, dot, line };
  };

  const removeMarker = () => {
    const scene = sceneRef.current;
    if (!scene || !markerRef.current) return;
    const { outerRing, innerRing, dot, line } = markerRef.current;
    scene.remove(outerRing);
    scene.remove(innerRing);
    scene.remove(dot);
    scene.remove(line);
    markerRef.current = null;
  };

  // ─── Exposition zoomToCity / resetView (comme onMapReady côté India) ───
  useEffect(() => {
    if (!onReady) return;
    onReady({
      zoomToCity: (city: FocusCity) => {
        const [x, z] = project(city.lat, city.lng);
        targetRef.current = { x, z };
        distRef.current = 400;
        updateCameraRef.current();
        addCityMarker(city.lat, city.lng);
      },
      resetView: () => {
        targetRef.current = { ...DEFAULT_TARGET };
        distRef.current = DEFAULT_DIST;
        thetaRef.current = DEFAULT_THETA;
        phiRef.current = DEFAULT_PHI;
        updateCameraRef.current();
        removeMarker();
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onReady]);

  // ─── Chargement des données (barres 3D) ───
  const loadRequestRef = useRef(0);
  const loadData = async () => {
    const requestId = ++loadRequestRef.current;

    const scene = sceneRef.current;
    if (!scene) return;

    if (barsRef.current.length > 0) {
      barsRef.current.forEach((bar) => {
        scene.remove(bar.mesh);
        scene.remove(bar.edges);
      });
      barsRef.current = [];
    }

    setLoading(true);
    onStats?.({ hexCount: 0, totalWeekly: 0, loading: true });

    try {
      const rows = await fetchUSMobilityHex(filters);
      if (requestId !== loadRequestRef.current) return;
      if (!rows || rows.length === 0) {
        const defaultLegend = DEFAULT_THRESHOLDS.map((t, i) => ({
          label: t.label,
          color: DEFAULT_COLORS[i],
          min: t.min,
          max: t.max,
        }));
        setLegendData(defaultLegend);
        setLoading(false);
        onStats?.({ hexCount: 0, totalWeekly: 0, loading: false });
        return;
      }

      // ── Légende par quantiles (mêmes effectifs, palette bleue) ──
      const weeklyValues = rows.map((r) => r.trips_weekly);
      const sorted = [...weeklyValues].sort((a, b) => a - b);
      const n = sorted.length;
      const numGroups = Math.min(7, n);
      let newLegendData: { label: string; color: string; min: number; max: number }[];

      if (numGroups === 1 || sorted[0] === sorted[n - 1]) {
        const val = Math.round(sorted[0]);
        const label = val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val.toString();
        newLegendData = [{ label, color: DEFAULT_COLORS[3], min: sorted[0], max: sorted[n - 1] }];
      } else {
        const groupSize = n / numGroups;
        let startIdx = 0;
        newLegendData = [];
        for (let i = 0; i < numGroups; i++) {
          const endIdx = Math.min(Math.round((i + 1) * groupSize) - 1, n - 1);
          const minVal = sorted[startIdx];
          const maxVal = sorted[endIdx];
          const minRounded = Math.round(minVal);
          const maxRounded = Math.round(maxVal);
          let label: string;
          if (minRounded === maxRounded) {
            label = minRounded >= 1000 ? `${(minRounded / 1000).toFixed(0)}k` : minRounded.toString();
          } else {
            const minLabel = minRounded >= 1000 ? `${(minRounded / 1000).toFixed(0)}k` : minRounded.toString();
            const maxLabel = maxRounded >= 1000 ? `${(maxRounded / 1000).toFixed(0)}k` : maxRounded.toString();
            label = `${minLabel} – ${maxLabel}`;
          }
          const t = i / (numGroups - 1);
          const color = DEFAULT_COLORS[i];
          newLegendData.push({ label, color, min: minVal, max: maxVal });
          startIdx = endIdx + 1;
        }
      }
      setLegendData(newLegendData);

      // ── Construction des barres 3D — hauteur ET couleur basées
      // uniquement sur SUM(total_weekly_trips), métrique unique demandée. ──
      const maxWeekly = Math.max(...rows.map((r) => r.trips_weekly));
      const totalWeekly = rows.reduce((acc, r) => acc + r.trips_weekly, 0);
      onStats?.({ hexCount: rows.length, totalWeekly, loading: false });

      const newBars: any[] = [];
      rows.forEach((r) => {
        const h3 = r.h3_destination;
        if (!h3) return;
        const weekly = r.trips_weekly;
        const tHeight = Math.sqrt(Math.max(weekly, 0) / (maxWeekly || 1));
        const h = Math.max(1.5, tHeight * 220);

        let lat = r.cent_lat;
        let lng = r.cent_lng;
        if (!lat || !lng) {
          try {
            const [la, ln] = cellToLatLng(h3);
            lat = la;
            lng = ln;
          } catch {
            return;
          }
        }
        const [x, z] = project(lat, lng);

        let hexColor = newLegendData[newLegendData.length - 1]?.color || DEFAULT_COLORS[6];
        for (const item of newLegendData) {
          if (weekly >= item.min && weekly <= item.max) {
            hexColor = item.color;
            break;
          }
        }

        const color = new THREE.Color(hexColor);
        const geo = new THREE.CylinderGeometry(7, 7, h, 6, 1);
        const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.92 });
        const mesh = new THREE.Mesh(geo, mat);
        mesh.position.set(x, h / 2, z);
        scene.add(mesh);

        const edges = new THREE.LineSegments(
          new THREE.EdgesGeometry(geo),
          new THREE.LineBasicMaterial({ color: 0x99b8d9, transparent: true, opacity: 0.3 })
        );
        edges.position.set(x, h / 2, z);
        scene.add(edges);

        newBars.push({ mesh, edges, weekly, city: r.city, state: r.state });
      });

      barsRef.current = newBars;
      setLoading(false);
    } catch (error) {
      console.error("Error loading US mobility data:", error);
      setLoading(false);
      onStats?.({ hexCount: 0, totalWeekly: 0, loading: false });
    }
  };

  // ─── Recharge quand les filtres changent ──
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  // ─── Initialisation de la scène 3D ──
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const W = canvas.offsetWidth, H = canvas.offsetHeight;
    const isLaptop =
  window.innerWidth >= 901 &&
  window.innerHeight <= 900;

if (isLaptop) {
  targetRef.current = {
    x: -900,
    z: 40,
  };
}
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x00313c, 1);
   renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.NoToneMapping;
    rendererRef.current = renderer;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x00313c);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(45, W / H, 1, 5000);
    cameraRef.current = camera;

    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.7);
    dirLight.position.set(300, 600, 200);
    scene.add(dirLight);
    const dirLight2 = new THREE.DirectionalLight(0x1a3d6b, 0.2);
    dirLight2.position.set(-400, 200, -300);
    scene.add(dirLight2);

    // ── Tuiles CARTO ──
    const centerTile = latLngToTile(CENTER_LAT, CENTER_LNG, ZOOM);
    const TILES_RANGE = 18;
    const loader = new THREE.TextureLoader();
    const n = Math.pow(2, ZOOM);
    const refTileX = ((CENTER_LNG + 180) / 360) * n;
    const refTileY =
      ((1 -
        Math.log(Math.tan((CENTER_LAT * Math.PI) / 180) + 1 / Math.cos((CENTER_LAT * Math.PI) / 180)) /
          Math.PI) /
        2) *
      n;

    for (let dy = -TILES_RANGE; dy <= TILES_RANGE; dy++) {
      for (let dx = -TILES_RANGE; dx <= TILES_RANGE; dx++) {
        const tx = centerTile.x + dx;
        const ty = centerTile.y + dy;
        const tex = loader.load(cartoBasemapTileUrl("a", ZOOM, tx, ty));
    tex.colorSpace = THREE.SRGBColorSpace;
        const worldX = (tx + 0.5 - refTileX) * WORLD_TILE;
        const worldZ = (ty + 0.5 - refTileY) * WORLD_TILE;
        const planeMesh = new THREE.Mesh(
          new THREE.PlaneGeometry(WORLD_TILE, WORLD_TILE),
          new THREE.MeshBasicMaterial({ map: tex })
        );
        planeMesh.rotation.x = -Math.PI / 2;
        planeMesh.position.set(worldX, -1, worldZ);
        scene.add(planeMesh);
      }
    }

    const updateCamera = () => {
      phiRef.current = Math.max(0.15, Math.min(1.5, phiRef.current));
      distRef.current = Math.max(120, Math.min(4200, distRef.current));
      const { x: tx, z: tz } = targetRef.current;
      camera.position.set(
        tx + distRef.current * Math.cos(phiRef.current) * Math.sin(thetaRef.current),
        distRef.current * Math.sin(phiRef.current),
        tz + distRef.current * Math.cos(phiRef.current) * Math.cos(thetaRef.current)
      );
      camera.lookAt(new THREE.Vector3(tx, 0, tz));
    };
    updateCameraRef.current = updateCamera;
    updateCamera();

    loadData();

    // ── Interactions souris ──
    let dragMode: "none" | "orbit" | "pan" = "none";
    let px = 0, py = 0;
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    const onDown = (e: MouseEvent) => {
      // Plain drag pans the map (scrolls freely left-right / up-down, like
      // a normal map) — shift or right-click orbits/tilts the 3D view.
      dragMode = e.button === 2 || e.shiftKey ? "orbit" : "pan";
      px = e.clientX;
      py = e.clientY;
      canvas.style.cursor = "grabbing";
    };

    const onMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      if (dragMode === "none") {
        raycaster.setFromCamera(mouse, camera);
        const meshes = barsRef.current.map((b) => b.mesh);
        const hits = raycaster.intersectObjects(meshes);
        if (hits.length > 0) {
          const bar = barsRef.current.find((b) => b.mesh === hits[0].object);
          if (bar) {
            if (hoveredMeshRef.current && hoveredMeshRef.current !== bar.mesh) {
              hoveredMeshRef.current.material.color.set(hoveredOrigColorRef.current);
              hoveredMeshRef.current.material.opacity = 1;
              hoveredMeshRef.current.material.transparent = false;
            }
            if (hoveredMeshRef.current !== bar.mesh) {
              hoveredOrigColorRef.current = bar.mesh.material.color.getHex();
              hoveredMeshRef.current = bar.mesh;
              bar.mesh.material.color.set(0xeb280f);
              bar.mesh.material.transparent = true;
              bar.mesh.material.opacity = 0.75;
            }
            setTooltip({ x: e.clientX, y: e.clientY, weekly: bar.weekly, city: bar.city, state: bar.state });
            canvas.style.cursor = "pointer";
          }
        } else {
          if (hoveredMeshRef.current) {
            hoveredMeshRef.current.material.color.set(hoveredOrigColorRef.current);
            hoveredMeshRef.current.material.opacity = 1;
            hoveredMeshRef.current.material.transparent = false;
            hoveredMeshRef.current = null;
          }
          setTooltip(null);
          canvas.style.cursor = "grab";
        }
        return;
      }

      const dx = e.clientX - px, dy = e.clientY - py;
      px = e.clientX;
      py = e.clientY;

      if (dragMode === "orbit") {
        thetaRef.current -= dx * 0.005;
        phiRef.current += dy * 0.005;
      } else {
        // Drag horizontal = pan (le contenu suit le curseur, donc on
        // SOUSTRAIT dx — l'ancien "+=" inversait le sens : tirer à droite
        // faisait partir la carte à gauche).
        // Drag vertical = effet 3D (bascule la caméra, phiRef), plus de
        // translation à plat sur cet axe.
        const panSpeed = distRef.current * 0.0012;
        targetRef.current.x -= Math.cos(thetaRef.current) * dx * panSpeed;
        targetRef.current.x = Math.max(-1600, Math.min(1600, targetRef.current.x));
        phiRef.current += dy * 0.005;
      }
      updateCamera();
    };

    const onUp = () => {
      dragMode = "none";
      canvas.style.cursor = "grab";
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      distRef.current += e.deltaY * 0.6;
      updateCamera();
    };

    const onCtx = (e: Event) => e.preventDefault();

    canvas.addEventListener("mousedown", onDown);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    if (allowWheelZoom) {
      canvas.addEventListener("wheel", onWheel, { passive: false });
    }
    canvas.addEventListener("contextmenu", onCtx);

    let lastTouchDist = 0;
    const onTS = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        dragMode = "pan";
        px = e.touches[0].clientX;
        py = e.touches[0].clientY;
      }
      if (e.touches.length === 2) {
        lastTouchDist = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
      }
    };
    const onTM = (e: TouchEvent) => {
      if (e.touches.length === 1 && dragMode === "pan") {
        const dx = e.touches[0].clientX - px, dy = e.touches[0].clientY - py;
        px = e.touches[0].clientX;
        py = e.touches[0].clientY;
        const panSpeed = distRef.current * 0.0012;
        targetRef.current.x -= Math.cos(thetaRef.current) * dx * panSpeed;
        targetRef.current.x = Math.max(-1600, Math.min(1600, targetRef.current.x));
        phiRef.current += dy * 0.005;
        updateCamera();
      }
      if (e.touches.length === 2) {
        const d = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
        distRef.current -= (d - lastTouchDist) * 1.5;
        lastTouchDist = d;
        updateCamera();
      }
    };
    canvas.addEventListener("touchstart", onTS);
    canvas.addEventListener("touchmove", onTM);
    canvas.addEventListener("touchend", onUp);

    const onResize = () => {
      const w = canvas.offsetWidth, h = canvas.offsetHeight;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    window.addEventListener("resize", onResize);

    let animId: number;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      canvas.removeEventListener("mousedown", onDown);
      if (allowWheelZoom) {
        canvas.removeEventListener("wheel", onWheel);
      }
      canvas.removeEventListener("contextmenu", onCtx);
      canvas.removeEventListener("touchstart", onTS);
      canvas.removeEventListener("touchmove", onTM);
      canvas.removeEventListener("touchend", onUp);
      renderer.dispose();
      sceneRef.current = null;
      cameraRef.current = null;
      rendererRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mmus">
      <canvas
        ref={canvasRef}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", cursor: "grab", touchAction: "none", zIndex: 1 }}
      />

      {tooltip && (
        <div
          style={{
            position: "fixed",
            left: tooltip.x + 14,
            top: tooltip.y + 14,
            zIndex: 2000,
            background: "rgba(0,20,25,0.96)",
            border: "1px solid rgba(61,151,204,0.4)",
            borderRadius: 12,
            padding: "12px 18px",
            pointerEvents: "none",
            fontFamily: "'ATR Innova',sans-serif",
            minWidth: 150,
            boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 700, color: "white", letterSpacing: -0.5, display: "flex", alignItems: "baseline", gap: 6 }}>
          </div>
          {tooltip.city && (
            <div style={{ fontSize: 18, color: "#009A84", fontWeight: 600, marginTop: 4 }}>
              {tooltip.city}{tooltip.state ? `, ${tooltip.state}` : ""}
            </div>
          )}
        </div>
      )}

      {/* ── Légende ── */}
      <div className="mmus-legend">
        <div className="mmus-legend-title">Weekly trips</div>
        {legendData.map((item) => (
          <div key={item.label} className="mmus-legend-item">
            <span className="mmus-legend-dot" style={{ background: item.color }} />
            {item.label}
          </div>
        ))}
      </div>

      {/* ── Contrôles de zoom ── */}
      <div className="mmus-zoom-controls">
        <button
          title="Zoom in"
          onClick={() => { distRef.current = Math.max(120, distRef.current - 150); updateCameraRef.current(); }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
        </button>
        <button
          title="Zoom out"
          onClick={() => { distRef.current = Math.min(4200, distRef.current + 150); updateCameraRef.current(); }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><path d="M5 12h14" /></svg>
        </button>
        <button
          title="Reset view"
          onClick={() => {
            distRef.current = DEFAULT_DIST;
            thetaRef.current = DEFAULT_THETA;
            phiRef.current = DEFAULT_PHI;
            targetRef.current = { ...DEFAULT_TARGET };
            updateCameraRef.current();
            removeMarker();
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
            <path d="M3 12a9 9 0 109-9 9.75 9.75 0 00-6.74 2.74L3 8" />
            <path d="M3 3v5h5" />
          </svg>
        </button>
      </div>

      <MapLoadingOverlay loading={loading} label="MobilityMonitor data" />
    </div>
  );
}
